/**
 * A worker's page for one location.
 *
 * Reached by a link the owner copies and sends: /w/<location id>. Somebody at
 * the Holy Ghost counter opens it and is at work — one place, the few tools
 * they are allowed, nothing else on screen.
 *
 * **The link is not a key.** It carries no secret and grants nothing. Anyone
 * can hold it; what decides the answer is who signs in. After login the page
 * asks, through the ordinary endpoints, which businesses this person belongs
 * to and which locations they may work in, and refuses when the location in
 * the URL is not one of them. Chidinma opening the Holy Ghost link sees a
 * refusal, not Holy Ghost, because the check is about her and not about the
 * address bar. Row level security would refuse her queries regardless — this
 * is the same answer given early, in words.
 *
 * The owner is unaffected: they keep the full dashboard at `/` and every
 * location in it. This is a different door into the same building, not a
 * different building.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bike,
  Boxes,
  ClipboardList,
  LayoutDashboard,
  Receipt,
  ShoppingBag,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import type { ModuleKey, Permission } from '@cenox/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, ErrorState, Spinner, cn } from '../../components/ui';
import { initials } from '../../lib/format';

interface MyLocations {
  canSeeAll: boolean;
  defaultBranchId: string | null;
  branches: Array<{ id: string; name: string; type: string; isDefault: boolean }>;
}

interface ModuleRow {
  key: ModuleKey;
  enabled: boolean;
}

/** What a worker can be sent to do. Ordered by how a shift actually runs. */
interface Tool {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  permission: Permission;
  module?: ModuleKey;
}

const TOOLS: Tool[] = [
  {
    to: '/shop',
    label: 'Record a sale',
    description: 'Scan or pick items and take payment',
    icon: ShoppingBag,
    permission: 'sales:create',
    module: 'inventory',
  },
  {
    to: '/deliveries/new',
    label: 'Record a delivery',
    description: 'Pick-up, drop-off, rider, fee and payment',
    icon: Bike,
    // The entry permission, not the reading one: this button is for doing the
    // job, and a worker who may only look should not be sent to a form.
    permission: 'deliveries:manage',
    module: 'delivery',
  },
  {
    to: '/deliveries',
    label: 'View deliveries',
    description: 'What has been recorded here',
    icon: ClipboardList,
    permission: 'deliveries:read',
    module: 'delivery',
  },
  {
    to: '/expenses',
    label: 'Record an expense',
    description: 'Money spent on behalf of the business',
    icon: Receipt,
    permission: 'expenses:create',
  },
  {
    to: '/receiving',
    label: 'Receive stock',
    description: 'Sign for a delivery against its invoice',
    icon: Truck,
    permission: 'receiving:manage',
    module: 'inventory',
  },
  {
    to: '/stock-counts',
    label: 'Count stock',
    description: 'Walk the shelf and record what is there',
    icon: ClipboardList,
    permission: 'stock:count',
    module: 'inventory',
  },
  {
    to: '/catalogue',
    label: 'Products',
    description: 'What this business sells',
    icon: Boxes,
    permission: 'products:read',
    module: 'inventory',
  },
];

/**
 * Which of the user's businesses owns this location, and may they work in it?
 *
 * Asked across their memberships because a person can belong to more than one
 * business, and the link says nothing about which. Every call goes through the
 * ordinary scoped endpoint, so the answer is the one row level security would
 * give — this only asks it sooner, so the worker gets a sentence instead of an
 * empty screen.
 */
function useWorkspace(branchId: string | undefined): {
  state: 'loading' | 'ready' | 'denied' | 'error';
  businessId?: string;
  businessName?: string;
  branchName?: string;
} {
  const { memberships } = useAuth();

  const query = useQuery({
    queryKey: ['workspace', branchId, memberships.map((m) => m.businessId).join(',')],
    enabled: Boolean(branchId) && memberships.length > 0,
    queryFn: async () => {
      for (const membership of memberships) {
        let mine: MyLocations;
        try {
          mine = await api.get<MyLocations>(`/businesses/${membership.businessId}/branches/mine`);
        } catch {
          // A membership that cannot even list its locations tells us nothing
          // about this one. Keep looking rather than failing the whole page.
          continue;
        }
        const hit = mine.branches.find((b) => b.id === branchId);
        if (hit) {
          return {
            businessId: membership.businessId,
            businessName: membership.businessName,
            branchName: hit.name,
          };
        }
      }
      return null;
    },
  });

  if (!branchId) return { state: 'error' };
  if (query.isPending) return { state: 'loading' };
  if (query.isError) return { state: 'error' };
  if (!query.data) return { state: 'denied' };
  return { state: 'ready', ...query.data };
}

export function WorkspacePage(): ReactNode {
  const { branchId } = useParams<{ branchId: string }>();
  const { user, can, logout, selectBusiness, business } = useAuth();
  const workspace = useWorkspace(branchId);
  const [switching, setSwitching] = useState(false);

  /**
   * Put the app in this location, for this worker.
   *
   * The link names a place, so the rest of the app should be in it — the till
   * sells from here, the expense is filed here, the count walks these shelves.
   * Writing the choice the switcher uses means every screen they reach agrees
   * with the link that sent them, without a second mechanism deciding it.
   */
  useEffect(() => {
    if (workspace.state !== 'ready' || !workspace.businessId || !branchId) return;
    try {
      window.localStorage.setItem(`cenox.location.${workspace.businessId}`, branchId);
    } catch {
      // Remembering is a convenience. The page works without it.
    }
    if (business?.businessId !== workspace.businessId) {
      setSwitching(true);
      void Promise.resolve(selectBusiness(workspace.businessId)).finally(() => setSwitching(false));
    }
  }, [workspace.state, workspace.businessId, branchId, business?.businessId, selectBusiness]);

  const modules = useQuery({
    queryKey: ['modules', workspace.businessId],
    enabled: workspace.state === 'ready' && Boolean(workspace.businessId),
    queryFn: () => api.get<{ data: ModuleRow[] }>(`/businesses/${workspace.businessId}/modules`),
    staleTime: 5 * 60 * 1000,
  });

  const active = useMemo(
    () => new Set((modules.data?.data ?? []).filter((m) => m.enabled).map((m) => m.key)),
    [modules.data],
  );
  /**
   * Only filter by module once the answer is actually in.
   *
   * Pending counts as unknown, not as "no modules". Treating an in-flight query
   * as an empty set hid the courier's only two buttons for the first moment of
   * every visit and then popped them in — and for a worker, who has no
   * `settings:read` and whose query never succeeds at all, it hid them for
   * good. Permissions are the gate; this is only tidying the menu.
   */
  const modulesKnown = !modules.isError && !modules.isPending;

  const tools = useMemo(
    () =>
      TOOLS.filter(
        (t) => can(t.permission) && (!t.module || !modulesKnown || active.has(t.module)),
      ),
    [can, active, modulesKnown],
  );

  if (workspace.state === 'loading' || switching) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Spinner label="Opening your workspace" />
      </div>
    );
  }

  /**
   * The refusal the whole feature exists to make.
   *
   * Deliberately plain about what happened and what to do, and deliberately
   * silent about the location: naming a place this person may not work in
   * would confirm it exists, and a refusal that leaks is only half a refusal.
   */
  if (workspace.state === 'denied') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md p-6 text-center">
          <h1 className="text-lg font-semibold text-slate-900">You are not assigned here</h1>
          <p className="mt-2 text-sm text-slate-600">
            This link is for a location you do not work in. If that is wrong, ask the owner to
            assign you to it — the link on its own cannot let you in.
          </p>
          <p className="mt-4 text-xs text-slate-500">Signed in as {user?.email}</p>
          <div className="mt-4 flex justify-center gap-2">
            <Link to="/business" className="text-sm font-medium text-indigo-700 hover:underline">
              Go to my dashboard
            </Link>
            <button type="button" onClick={logout} className="text-sm text-slate-500 hover:underline">
              Sign out
            </button>
          </div>
        </Card>
      </div>
    );
  }

  if (workspace.state === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <ErrorState message="That workspace link could not be opened." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            {/* The place comes first. It is the one fact that makes this page theirs. */}
            <p className="truncate text-lg font-semibold text-slate-900">{workspace.branchName}</p>
            <p className="truncate text-xs text-slate-500">{workspace.businessName}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span
              className="grid size-8 place-items-center rounded-full bg-slate-100 text-xs font-medium text-slate-600"
              title={user?.email}
            >
              {initials(user?.fullName ?? user?.email ?? '?')}
            </span>
            <button type="button" onClick={logout} className="text-xs text-slate-500 hover:underline">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        <h1 className="text-sm font-medium text-slate-500">What would you like to do?</h1>

        {tools.length === 0 ? (
          <Card className="mt-3 p-6 text-center">
            <p className="text-sm text-slate-600">
              You are assigned to {workspace.branchName}, but have not been given any tools to use
              yet. Ask the owner to grant what you need.
            </p>
          </Card>
        ) : (
          <ul className="mt-3 grid gap-3 sm:grid-cols-2">
            {tools.map((tool) => (
              <li key={tool.to}>
                <Link
                  to={tool.to}
                  className={cn(
                    'flex h-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-4',
                    'transition-colors hover:border-indigo-300 hover:bg-indigo-50/40',
                  )}
                >
                  <tool.icon className="mt-0.5 size-5 shrink-0 text-indigo-600" aria-hidden />
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-900">{tool.label}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">{tool.description}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {/*
          Owners reach the same link and should not be trapped in it. Shown only
          to somebody who actually has the dashboard, so a counter hand is not
          offered a door that closes in their face.
        */}
        {can('reports:read') && (
          <Link
            to="/business"
            className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"
          >
            <LayoutDashboard className="size-4" aria-hidden />
            Open the full dashboard
          </Link>
        )}
      </main>
    </div>
  );
}
